import {
  ArcElement,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  Title,
  Tooltip,
} from 'chart.js';
import React, { useEffect, useMemo, useState } from 'react';
import { Bar, Doughnut } from 'react-chartjs-2';
import { useScreenLogging } from '../../../hooks/useScreenLogging';
import { typography } from '../../../styles/typography';
import { getAllSites } from '../../SiteManagement/siteApi';
import Card from '../../ui/card';
import { fetchDishDataset } from './RestorantSiteApi';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

const formatDateForAPI = (date) => date.toISOString().split('T')[0];
const formatDisplayDate = (dateStr) => {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  return d.toLocaleString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const statusColors = {
  preparing: '#f59e0b',
  delivered: '#22c55e',
  pending: '#94a3b8',
  cooking: '#3b82f6',
  served: '#8b5cf6',
  cancelled: '#ef4444',
};

const getStatusColor = (status) =>
  statusColors[status] || '#64748b';

/** Dummy dish dataset for display when API is unavailable or returns empty */
const getDummyDishData = () => [
  {
    order_event_timestamp: '2025-04-14T12:30:00',
    table_no: '5',
    order_ended: false,
    server_user_id: 'usr-abc-123',
    site_id: 'site-xyz-001',
    ss_id: 'ss-001',
    server_employee_id: 'EMP-101',
    server_user_name: 'John Doe',
    site_name: 'Main Dining',
    site_address: '123 Restaurant Ave',
    order_item_id: 'oi-001',
    dish_id: 'dish-001',
    notes: 'No onions',
    qty: 2,
    current_status: 'preparing',
    cust_mods: ['extra cheese'],
    stages: { chef_assigned: 'EMP-202' },
    dish_info: {
      name: 'Margherita Pizza',
      cuisine: 'Italian',
      preference: 'vegetarian',
      Price: 12.99,
      type: 'main',
    },
  },
  {
    order_event_timestamp: '2025-04-14T12:30:00',
    table_no: '5',
    order_ended: false,
    server_user_id: 'usr-abc-123',
    site_id: 'site-xyz-001',
    ss_id: 'ss-001',
    server_employee_id: 'EMP-101',
    server_user_name: 'John Doe',
    site_name: 'Main Dining',
    site_address: '123 Restaurant Ave',
    order_item_id: 'oi-002',
    dish_id: 'dish-002',
    notes: null,
    qty: 1,
    current_status: 'delivered',
    cust_mods: null,
    stages: {},
    dish_info: {
      name: 'Caesar Salad',
      cuisine: 'American',
      preference: null,
      Price: 8.5,
      type: 'starter',
    },
  },
  {
    order_event_timestamp: '2025-04-14T13:15:00',
    table_no: '3',
    order_ended: false,
    server_user_id: 'usr-def-456',
    site_id: 'site-xyz-001',
    ss_id: 'ss-002',
    server_employee_id: 'EMP-102',
    server_user_name: 'Jane Smith',
    site_name: 'Main Dining',
    site_address: '123 Restaurant Ave',
    order_item_id: 'oi-003',
    dish_id: 'dish-003',
    notes: 'Well done',
    qty: 1,
    current_status: 'cooking',
    cust_mods: null,
    stages: { chef_assigned: 'EMP-202' },
    dish_info: {
      name: 'Grilled Chicken',
      cuisine: 'American',
      preference: null,
      Price: 15.99,
      type: 'main',
    },
  },
  {
    order_event_timestamp: '2025-04-14T13:20:00',
    table_no: '7',
    order_ended: false,
    server_user_id: 'usr-def-456',
    site_id: 'site-xyz-001',
    ss_id: 'ss-002',
    server_employee_id: 'EMP-102',
    server_user_name: 'Jane Smith',
    site_name: 'Main Dining',
    site_address: '123 Restaurant Ave',
    order_item_id: 'oi-004',
    dish_id: 'dish-004',
    notes: null,
    qty: 2,
    current_status: 'pending',
    cust_mods: ['no ice'],
    stages: {},
    dish_info: {
      name: 'Mango Smoothie',
      cuisine: 'Beverages',
      preference: null,
      Price: 6.5,
      type: 'beverage',
    },
  },
  {
    order_event_timestamp: '2025-04-14T14:00:00',
    table_no: '2',
    order_ended: false,
    server_user_id: 'usr-abc-123',
    site_id: 'site-xyz-001',
    ss_id: 'ss-001',
    server_employee_id: 'EMP-101',
    server_user_name: 'John Doe',
    site_name: 'Main Dining',
    site_address: '123 Restaurant Ave',
    order_item_id: 'oi-005',
    dish_id: 'dish-005',
    notes: null,
    qty: 1,
    current_status: 'served',
    cust_mods: null,
    stages: {},
    dish_info: {
      name: 'Chocolate Brownie',
      cuisine: 'Desserts',
      preference: null,
      Price: 7.99,
      type: 'dessert',
    },
  },
  {
    order_event_timestamp: '2025-04-14T14:10:00',
    table_no: '4',
    order_ended: false,
    server_user_id: 'usr-ghi-789',
    site_id: 'site-xyz-001',
    ss_id: 'ss-003',
    server_employee_id: 'EMP-103',
    server_user_name: 'Mike Wilson',
    site_name: 'Main Dining',
    site_address: '123 Restaurant Ave',
    order_item_id: 'oi-006',
    dish_id: 'dish-001',
    notes: null,
    qty: 1,
    current_status: 'delivered',
    cust_mods: null,
    stages: {},
    dish_info: {
      name: 'Margherita Pizza',
      cuisine: 'Italian',
      preference: 'vegetarian',
      Price: 12.99,
      type: 'main',
    },
  },
  {
    order_event_timestamp: '2025-04-14T11:45:00',
    table_no: '1',
    order_ended: false,
    server_user_id: 'usr-abc-123',
    site_id: 'site-xyz-001',
    ss_id: 'ss-001',
    server_employee_id: 'EMP-101',
    server_user_name: 'John Doe',
    site_name: 'Main Dining',
    site_address: '123 Restaurant Ave',
    order_item_id: 'oi-007',
    dish_id: 'dish-006',
    notes: 'Extra sauce',
    qty: 3,
    current_status: 'preparing',
    cust_mods: ['extra cheese', 'jalapeños'],
    stages: { chef_assigned: 'EMP-202' },
    dish_info: {
      name: 'Pepperoni Pizza',
      cuisine: 'Italian',
      preference: null,
      Price: 14.99,
      type: 'main',
    },
  },
  {
    order_event_timestamp: '2025-04-14T12:00:00',
    table_no: '6',
    order_ended: false,
    server_user_id: 'usr-def-456',
    site_id: 'site-xyz-001',
    ss_id: 'ss-002',
    server_employee_id: 'EMP-102',
    server_user_name: 'Jane Smith',
    site_name: 'Main Dining',
    site_address: '123 Restaurant Ave',
    order_item_id: 'oi-008',
    dish_id: 'dish-007',
    notes: null,
    qty: 1,
    current_status: 'cancelled',
    cust_mods: null,
    stages: {},
    dish_info: {
      name: 'Fish & Chips',
      cuisine: 'British',
      preference: null,
      Price: 13.5,
      type: 'main',
    },
  },
];

const BusinessAnalytics = () => {
  // Log screen view
  useScreenLogging('RestorantSiteAnalytics', {
    component: 'RestorantSiteAnalytics',
  });
  const [sites, setSites] = useState([]);
  const [isSitesLoading, setIsSitesLoading] = useState(true);
  const [sitesError, setSitesError] = useState(null);
  const [selectedSite, setSelectedSite] = useState('');
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 7);
    return formatDateForAPI(d);
  });
  const [endDate, setEndDate] = useState(() => formatDateForAPI(new Date()));
  const [isLoading, setIsLoading] = useState(false);
  const [dishData, setDishData] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadSites = async () => {
      setIsSitesLoading(true);
      setSitesError(null);
      try {
        const response = await getAllSites();
        const list = Array.isArray(response?.sites)
          ? response.sites
          : Array.isArray(response)
          ? response
          : [];
        const sorted = [...list].sort((a, b) =>
          (a?.site_name || a?.name || '').localeCompare(
            b?.site_name || b?.name || ''
          )
        );
        setSites(sorted);
        // Don't auto-select - let user select from dropdown
      } catch (err) {
        console.error('Restorant Site Analytics: Error fetching sites', err);
        setSitesError(err?.message || 'Unable to load sites.');
      } finally {
        setIsSitesLoading(false);
      }
    };
    loadSites();
  }, []);

  useEffect(() => {
    if (!selectedSite || !startDate || !endDate) {
      setDishData([]);
      setError(null);
      return;
    }

    if (selectedSite === '__demo__') {
      setError(null);
      setIsLoading(false);
      setDishData(getDummyDishData());
      return;
    }

    const loadDishDataset = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const data = await fetchDishDataset({
          siteId: selectedSite,
          startDate: new Date(startDate),
          endDate: new Date(endDate),
        });
        const items = Array.isArray(data) ? data : [];
        setDishData(items.length > 0 ? items : getDummyDishData());
      } catch (err) {
        console.error('Restorant Site Analytics: Error fetching dish dataset', err);
        setError(err?.message || 'Failed to load dish dataset.');
        setDishData(getDummyDishData());
      } finally {
        setIsLoading(false);
      }
    };

    loadDishDataset();
  }, [selectedSite, startDate, endDate]);

  const summary = useMemo(() => {
    let totalItems = dishData.length;
    let totalQty = 0;
    let estimatedRevenue = 0;
    const statusCounts = {};

    dishData.forEach((row) => {
      const qty = Number(row.qty) || 0;
      totalQty += qty;
      const price = Number(row?.dish_info?.Price) || 0;
      estimatedRevenue += qty * price;
      const status = (row.current_status || 'unknown').toLowerCase();
      statusCounts[status] = (statusCounts[status] || 0) + 1;
    });

    return {
      totalItems,
      totalQty,
      estimatedRevenue,
      statusCounts,
    };
  }, [dishData]);

  const statusChartData = useMemo(() => {
    const counts = summary.statusCounts;
    const entries = Object.entries(counts);
    if (entries.length === 0) return null;
    return {
      labels: entries.map(([k]) => k),
      datasets: [
        {
          data: entries.map(([, v]) => v),
          backgroundColor: entries.map(([k]) => getStatusColor(k)),
        },
      ],
    };
  }, [summary.statusCounts]);

  const topDishesData = useMemo(() => {
    const byDish = {};
    dishData.forEach((row) => {
      const name = row?.dish_info?.name || row.dish_id || 'Unknown';
      byDish[name] = (byDish[name] || 0) + (Number(row.qty) || 0);
    });
    const sorted = Object.entries(byDish)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10);
    if (sorted.length === 0) return null;
    return {
      labels: sorted.map(([name]) => name),
      datasets: [
        {
          label: 'Quantity',
          data: sorted.map(([, qty]) => qty),
          backgroundColor: '#22c55e',
        },
      ],
    };
  }, [dishData]);

  const revenueByTypeData = useMemo(() => {
    const byType = {};
    dishData.forEach((row) => {
      const type = row?.dish_info?.type || 'other';
      const price = Number(row?.dish_info?.Price) || 0;
      const qty = Number(row.qty) || 0;
      byType[type] = (byType[type] || 0) + price * qty;
    });
    const entries = Object.entries(byType);
    if (entries.length === 0) return null;
    return {
      labels: entries.map(([k]) => k),
      datasets: [
        {
          label: 'Revenue',
          data: entries.map(([, v]) => v),
          backgroundColor: ['#3b82f6', '#8b5cf6', '#f59e0b', '#06b6d4', '#ec4899'],
        },
      ],
    };
  }, [dishData]);

  const barOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: true, position: 'top' },
      tooltip: {
        callbacks: {
          label: (ctx) =>
            ctx.dataset.label === 'Revenue'
              ? `₹${ctx.parsed.y.toLocaleString('en-IN')}`
              : `${ctx.parsed.y} qty`,
        },
      },
    },
    scales: {
      x: { ticks: { maxRotation: 45 } },
      y: { beginAtZero: true },
    },
  };

  const pieOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: true, position: 'right' },
      tooltip: {
        callbacks: {
          label: (ctx) => {
            const total = ctx.dataset.data.reduce((a, b) => a + b, 0);
            const pct = total ? ((ctx.parsed / total) * 100).toFixed(1) : 0;
            return `${ctx.label}: ${ctx.parsed} (${pct}%)`;
          },
        },
      },
    },
  };

  return (
    <div className="w-full h-full">
      <div className="mb-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className={`${typography.h2} text-black mb-1`}>
            Restorant Site Analytics
          </h1>
          <p className={`${typography.p} text-gray-600 text-sm`}>
            Dish & order analytics by site and date range
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="h-10 px-4 bg-white rounded-lg border border-gray-300 focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500/40 text-sm"
          />
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="h-10 px-4 bg-white rounded-lg border border-gray-300 focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500/40 text-sm"
          />
          <select
            value={selectedSite}
            onChange={(e) => setSelectedSite(e.target.value)}
            disabled={isSitesLoading}
            className="h-10 px-4 bg-white rounded-lg border border-gray-300 focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500/40 text-sm"
          >
            {isSitesLoading ? (
              <option>Loading sites...</option>
            ) : sitesError ? (
              <option>Error</option>
            ) : (
              <>
                <option value="">Select a site</option>
                <option value="__demo__">Demo data</option>
                {sites.map((site) => {
                  const id =
                    site?.site_id ?? site?.siteId ?? site?.id ?? site?._id ?? '';
                  const name = site?.site_name || site?.name || `Site ${id}`;
                  return (
                    <option key={id} value={String(id)}>
                      {name}
                    </option>
                  );
                })}
              </>
            )}
          </select>
        </div>
      </div>

      {error && !isLoading && (
        <Card.Gradient className="p-6 mb-6 border border-red-200 bg-red-50">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <svg
                className="w-6 h-6 text-red-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <div>
                <h3
                  className={typography.h5}
                  style={{ color: '#dc2626', marginBottom: '0.25rem' }}
                >
                  Error loading data
                </h3>
                <p className={typography.p} style={{ color: '#991b1b' }}>
                  {error}
                </p>
              </div>
            </div>
            <button
              onClick={() => setError(null)}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm font-medium"
            >
              Dismiss
            </button>
          </div>
        </Card.Gradient>
      )}

      {!selectedSite && (
        <Card.Gradient className="p-8 text-center">
          <p className={typography.p} style={{ color: '#666' }}>
            Select a site and date range to view dish analytics.
          </p>
        </Card.Gradient>
      )}

      {selectedSite && isLoading && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2 mb-6">
            {[1, 2, 3, 4].map((i) => (
              <Card.Gradient key={i} className="p-5">
                <div className="h-4 bg-gray-200 rounded animate-pulse mb-2 w-2/3" />
                <div className="h-8 bg-gray-200 rounded animate-pulse w-3/4" />
              </Card.Gradient>
            ))}
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card.Gradient className="p-6">
              <div className="h-6 bg-gray-200 rounded animate-pulse mb-4 w-1/3" />
              <div className="h-[300px] bg-gray-100 rounded animate-pulse" />
            </Card.Gradient>
            <Card.Gradient className="p-6">
              <div className="h-6 bg-gray-200 rounded animate-pulse mb-4 w-1/3" />
              <div className="h-[300px] bg-gray-100 rounded animate-pulse" />
            </Card.Gradient>
          </div>
        </div>
      )}

      {selectedSite && !isLoading && dishData.length > 0 && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2 mb-6">
            <Card.Gradient
              className="p-5"
              style={{
                background: 'linear-gradient(to bottom right, #3b82f6, #2563eb)',
                boxShadow: '0px 15px 30px 0px rgba(0,0,0,0.10)',
                color: '#ffffff',
              }}
            >
              <p className="text-sm opacity-90 mb-1">Order items</p>
              <p className="text-2xl font-bold">{summary.totalItems}</p>
              <p className="text-xs opacity-75 mt-1">In date range</p>
            </Card.Gradient>
            <Card.Gradient
              className="p-5"
              style={{
                background: 'linear-gradient(to bottom right, #22c55e, #16a34a)',
                boxShadow: '0px 15px 30px 0px rgba(0,0,0,0.10)',
                color: '#ffffff',
              }}
            >
              <p className="text-sm opacity-90 mb-1">Total quantity</p>
              <p className="text-2xl font-bold">{summary.totalQty}</p>
              <p className="text-xs opacity-75 mt-1">Dishes</p>
            </Card.Gradient>
            <Card.Gradient
              className="p-5"
              style={{
                background: 'linear-gradient(to bottom right, #a855f7, #9333ea)',
                boxShadow: '0px 15px 30px 0px rgba(0,0,0,0.10)',
                color: '#ffffff',
              }}
            >
              <p className="text-sm opacity-90 mb-1">Est. revenue</p>
              <p className="text-2xl font-bold">
                ₹{summary.estimatedRevenue.toLocaleString('en-IN')}
              </p>
              <p className="text-xs opacity-75 mt-1">qty × price</p>
            </Card.Gradient>
            <Card.Gradient
              className="p-5"
              style={{
                background: 'linear-gradient(to bottom right, #f97316, #ea580c)',
                boxShadow: '0px 15px 30px 0px rgba(0,0,0,0.10)',
                color: '#ffffff',
              }}
            >
              <p className="text-sm opacity-90 mb-1">Statuses</p>
              <p className="text-2xl font-bold">
                {Object.keys(summary.statusCounts).length}
              </p>
              <p className="text-xs opacity-75 mt-1">Unique</p>
            </Card.Gradient>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            {statusChartData && (
              <Card.Gradient className="p-6">
                <h3
                  className={typography.h5}
                  style={{ color: '#000', marginBottom: '1rem' }}
                >
                  Order status
                </h3>
                <div style={{ height: '300px' }}>
                  <Doughnut data={statusChartData} options={pieOptions} />
                </div>
              </Card.Gradient>
            )}
            {topDishesData && (
              <Card.Gradient className="p-6">
                <h3
                  className={typography.h5}
                  style={{ color: '#000', marginBottom: '1rem' }}
                >
                  Top dishes by quantity
                </h3>
                <div style={{ height: '300px' }}>
                  <Bar data={topDishesData} options={barOptions} />
                </div>
              </Card.Gradient>
            )}
          </div>

          {revenueByTypeData && (
            <Card.Gradient className="p-6 mb-6">
              <h3
                className={typography.h5}
                style={{ color: '#000', marginBottom: '1rem' }}
              >
                Revenue by dish type
              </h3>
              <div style={{ height: '300px' }}>
                <Bar data={revenueByTypeData} options={barOptions} />
              </div>
            </Card.Gradient>
          )}

          <Card.Gradient className="p-6 overflow-hidden">
            <h3
              className={typography.h5}
              style={{ color: '#000', marginBottom: '1rem' }}
            >
              Order items
            </h3>
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className={typography.sh2 + ' py-3 px-3 text-gray-700'}>
                      Time
                    </th>
                    <th className={typography.sh2 + ' py-3 px-3 text-gray-700'}>
                      Table
                    </th>
                    <th className={typography.sh2 + ' py-3 px-3 text-gray-700'}>
                      Server
                    </th>
                    <th className={typography.sh2 + ' py-3 px-3 text-gray-700'}>
                      Dish
                    </th>
                    <th className={typography.sh2 + ' py-3 px-3 text-gray-700'}>
                      Qty
                    </th>
                    <th className={typography.sh2 + ' py-3 px-3 text-gray-700'}>
                      Status
                    </th>
                    <th className={typography.sh2 + ' py-3 px-3 text-gray-700'}>
                      Line total
                    </th>
                    <th className={typography.sh2 + ' py-3 px-3 text-gray-700'}>
                      Notes
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {dishData.map((row, idx) => {
                    const price = Number(row?.dish_info?.Price) || 0;
                    const qty = Number(row.qty) || 0;
                    const lineTotal = price * qty;
                    return (
                      <tr
                        key={row.order_item_id + '-' + idx}
                        className="border-b border-gray-100 hover:bg-gray-50/50"
                      >
                        <td className="py-2 px-3 text-sm text-gray-800">
                          {formatDisplayDate(row.order_event_timestamp)}
                        </td>
                        <td className="py-2 px-3 text-sm">
                          {row.table_no ?? '—'}
                        </td>
                        <td className="py-2 px-3 text-sm">
                          {row.server_user_name ?? row.server_employee_id ?? '—'}
                        </td>
                        <td className="py-2 px-3 text-sm font-medium">
                          {row?.dish_info?.name ?? row.dish_id ?? '—'}
                        </td>
                        <td className="py-2 px-3 text-sm">{qty}</td>
                        <td className="py-2 px-3">
                          <span
                            className="inline-block px-2 py-0.5 rounded text-xs font-medium text-white"
                            style={{
                              backgroundColor: getStatusColor(
                                (row.current_status || '').toLowerCase()
                              ),
                            }}
                          >
                            {row.current_status ?? '—'}
                          </span>
                        </td>
                        <td className="py-2 px-3 text-sm">
                          ₹{lineTotal.toLocaleString('en-IN')}
                        </td>
                        <td className="py-2 px-3 text-sm text-gray-600 max-w-[180px] truncate">
                          {row.notes ?? '—'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card.Gradient>
        </>
      )}

      {selectedSite && !isLoading && dishData.length === 0 && !error && (
        <Card.Gradient className="p-12 text-center">
          <div className="max-w-md mx-auto">
            <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg
                className="w-10 h-10 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                />
              </svg>
            </div>
            <h3
              className={typography.h4}
              style={{ color: '#000', marginBottom: '0.5rem' }}
            >
              No dish data in this range
            </h3>
            <p className={typography.p} style={{ color: '#666' }}>
              Try a different site or date range
            </p>
          </div>
        </Card.Gradient>
      )}
    </div>
  );
};

export default BusinessAnalytics;
